import { useNavigate, useParams } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { tabStore } from '@/features/tabs/tabs.store';
import { TeamSidebar } from '@/features/team/components/TeamSidebar';
import { teamStore } from '@/features/team/team.store';
import { parseTeamTab } from '@/features/team/team.types';
import { cn } from '@/lib/utils';
import { Routes } from '@/types/routes';
import { inBrowser } from '@/utils';

import { SettingsMenu } from './settings-menu';
import { openSettings } from './settings-navigation';
import { SettingsSidebarSearch } from './settings-sidebar-search';
import { useSettingsDeepLinkTarget } from './useSettingsDeepLink';

import { settingsApi } from '.';

const TEAM_SETTINGS_PATH = `${Routes.Settings}/Team`;

export const SettingsSidebar = observer(() => {
  const { section } = useParams({ strict: false });
  const showsTeam = section === 'Team' && teamStore.dashboard !== null;

  return (
    <div className="relative min-h-0 flex-1 overflow-x-clip">
      <XStack
        className={cn(
          'h-full w-full transition-transform duration-300 ease-out motion-reduce:transition-none',
          showsTeam && '-translate-x-full'
        )}
      >
        <YStack className="h-full w-full shrink-0" inert={showsTeam}>
          <SettingsPane />
        </YStack>
        <YStack className="h-full w-full shrink-0" inert={!showsTeam}>
          <TeamPane />
        </YStack>
      </XStack>
    </div>
  );
});

const SettingsPane = () => {
  const currentVersion = settingsApi.useAppVersion();

  return (
    <YStack className="h-full w-full min-h-0 gap-2">
      <SettingsSidebarSearch>
        <ScrollMask
          className="flex-1 min-h-0 w-full overflow-x-hidden py-2 scrollbar-hide"
          showEndFade
        >
          <SettingsMenu />
        </ScrollMask>
      </SettingsSidebarSearch>
      {!inBrowser() && (
        <p className="shrink-0 px-[var(--row-pad)] pb-3 text-xs text-foreground-muted opacity-50">
          Littlebird v{currentVersion}
        </p>
      )}
    </YStack>
  );
};

const TeamPane = observer(() => {
  const navigate = useNavigate();
  const { item } = useSettingsDeepLinkTarget();
  const dashboard = teamStore.dashboard;
  if (!dashboard) return null;

  const backToSettings = () => {
    const tab = tabStore.activeTab;
    const previousRoute = tab?.history
      ?.slice(0, tab.historyIndex)
      .reverse()
      .find((route) => route.path !== TEAM_SETTINGS_PATH);
    if (previousRoute?.path.startsWith(Routes.Settings)) {
      void navigate({
        to: previousRoute.path,
        search: previousRoute.search as Record<string, string>,
      });
    } else {
      void openSettings('General');
    }
  };

  return (
    <TeamSidebar
      dashboard={dashboard}
      activeTab={parseTeamTab(item, teamStore.isAdmin)}
      isAdmin={teamStore.isAdmin}
      onTabChange={(tab) => openSettings('Team', tab)}
      onBack={backToSettings}
    />
  );
});
